import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../../lib/api";
import { TID } from "../../lib/testIds";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";

export default function AddRouteDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({
    alias: "",
    target_type: "web_target",   // default to the new preferred mode
    web_target_id: "",
    engine_id: "",
    model_id: "",
    notes: "",
  });
  const [engines, setEngines] = useState([]);
  const [webTargets, setWebTargets] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [e, w] = await Promise.all([
          api.get("/gateway/engines"),
          api.get("/gateway/web-targets"),
        ]);
        setEngines(e.data || []);
        setWebTargets(w.data || []);
      } catch (err) {
        toast.error(apiErrorMessage(err));
      }
    })();
  }, []);

  const modelsForEngine = useMemo(() => {
    const e = engines.find((x) => x.id === form.engine_id);
    return e?.models || [];
  }, [engines, form.engine_id]);

  const submit = async () => {
    if (!/^[a-z0-9\-]{1,64}$/.test(form.alias)) {
      toast.error("Alias inválido. Solo minúsculas, dígitos y guiones (1-64).");
      return;
    }
    if (form.target_type === "web_target" && !form.web_target_id) {
      toast.error("Selecciona un Web Target.");
      return;
    }
    if (form.target_type === "engine" && (!form.engine_id || !form.model_id)) {
      toast.error("Selecciona motor API y modelo (modo legacy).");
      return;
    }
    setSaving(true);
    try {
      const body = { alias: form.alias, target_type: form.target_type, notes: form.notes };
      if (form.target_type === "web_target") {
        body.web_target_id = form.web_target_id;
      } else {
        body.engine_id = form.engine_id;
        body.model_id = form.model_id;
      }
      await api.post("/gateway/routes", body);
      toast.success("Ruta creada");
      onSaved();
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const isWeb = form.target_type === "web_target";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid={TID.gateway.routeDialog}
                     className="bg-zinc-950 border-zinc-800 max-w-lg">
        <DialogHeader>
          <DialogTitle>Nueva ruta</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Alias público</Label>
            <Input
              data-testid={TID.gateway.routeField("alias")}
              value={form.alias}
              onChange={(e) => setForm({ ...form, alias: e.target.value.toLowerCase() })}
              placeholder="krai-gemini"
              className="bg-zinc-900 border-zinc-800 font-mono text-sm"
            />
            <div className="text-[10px] text-zinc-500">
              Tus apps enviarán <code>{`{"model":"${form.alias || "..."}"}`}</code>.
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Tipo de destino</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid={TID.gateway.routeTargetType("web_target")}
                onClick={() => setForm({ ...form, target_type: "web_target" })}
                className={
                  "rounded-md border px-3 py-2 text-xs text-left transition " +
                  (isWeb
                    ? "border-white bg-zinc-800 text-white"
                    : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-white")
                }
              >
                <div className="font-medium">Web Target</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  Recomendado. Vía sesión web + Playwright.
                </div>
              </button>
              <button
                data-testid={TID.gateway.routeTargetType("engine")}
                onClick={() => setForm({ ...form, target_type: "engine" })}
                className={
                  "rounded-md border px-3 py-2 text-xs text-left transition " +
                  (!isWeb
                    ? "border-white bg-zinc-800 text-white"
                    : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-white")
                }
              >
                <div className="font-medium">Motor API (legacy)</div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  Adaptador deshabilitado — solo mantenido para retrocompat.
                </div>
              </button>
            </div>
          </div>

          {isWeb ? (
            <div className="space-y-1.5">
              <Label className="text-xs text-zinc-400">Web Target</Label>
              <Select value={form.web_target_id || undefined}
                      onValueChange={(v) => setForm({ ...form, web_target_id: v })}>
                <SelectTrigger data-testid={TID.gateway.routeField("web_target_id")}
                               className="bg-zinc-900 border-zinc-800">
                  <SelectValue placeholder={webTargets.length ? "Elige…" : "Crea un web target primero"} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-zinc-800">
                  {webTargets.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.name} <span className="text-zinc-500 font-mono">({t.id})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <>
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-400">Motor API</Label>
                <Select value={form.engine_id || undefined}
                        onValueChange={(v) => setForm({ ...form, engine_id: v, model_id: "" })}>
                  <SelectTrigger data-testid={TID.gateway.routeField("engine_id")}
                                 className="bg-zinc-900 border-zinc-800">
                    <SelectValue placeholder={engines.length ? "Elige…" : "No hay motores"} />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {engines.map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.name} <span className="text-zinc-500 font-mono">({e.id})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-zinc-400">Modelo</Label>
                <Select value={form.model_id || undefined}
                        onValueChange={(v) => setForm({ ...form, model_id: v })}
                        disabled={!form.engine_id}>
                  <SelectTrigger data-testid={TID.gateway.routeField("model_id")}
                                 className="bg-zinc-900 border-zinc-800">
                    <SelectValue placeholder="Elige…" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {modelsForEngine.map((m) => (
                      <SelectItem key={m.id} value={m.id}>
                        {m.name || m.id} <span className="text-zinc-500 font-mono">({m.id})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          <div className="space-y-1.5">
            <Label className="text-xs text-zinc-400">Notas</Label>
            <Textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="bg-zinc-900 border-zinc-800 text-sm resize-none"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={onClose}>Cancelar</Button>
          <Button
            data-testid={TID.gateway.routeSubmit}
            onClick={submit}
            disabled={saving}
            size="sm"
            className="bg-white text-black hover:bg-zinc-200"
          >
            {saving ? "Guardando…" : "Crear ruta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
